import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {axiosInstance} from '../services/Api';

const initialState = {
  shops: [],
  pagination: {},  // For storing pagination info
  selectedShop: null,
  loading: false,
  error: null,
  message: null,
};
// Async thunk to fetch shops
export const fetchShops = createAsyncThunk(
  'shops/fetchShops',
  async ({ page = 1, limit = 10, users }, { rejectWithValue }) => {
    try {
      // Initialize params object with pagination settings
      const params = { page, limit };
      // Add users filter to params if users is provided
      if (users) {
        // If users is an array, join them into a comma-separated string
        // If it's already a string, keep it as is
        params.users = Array.isArray(users) ? users.join(",") : users;
      }
      
      // Make the POST request with params in the body
      const response = await axiosInstance.post('/shops/getShops', params);

      return response.data;
    } catch (error) {
      // Handle error and reject with a value
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);




// Async thunk to add a shop
export const addShop = createAsyncThunk(
  'shops/addShop',
  async (shopData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/shops', shopData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data.message|| 'An error occurred');
    }
  }
);


// Async thunk to fetch a single shop by ID
export const fetchShopById = createAsyncThunk(
  'shops/fetchShopById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/shops/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const updateShop = createAsyncThunk(
  'shops/updateShop',
  async (shopData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/shops', shopData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);
// Delete a shop by ID
export const deleteShop = createAsyncThunk(
  'shops/deleteShop',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/shops/${id}`);
      return id; // Retourner l'ID pour la suppression locale
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

const shopSlice = createSlice({
  name: 'shops',
  initialState,
  reducers: {
    setSelectedShop: (state, action) => {
      state.selectedShop = action.payload;
    },
    clearSelectedShop: (state) => {
      state.selectedShop = null;
    },
    clearAlerts: (state) => {
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchShops.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchShops.fulfilled, (state, action) => {
        state.loading = false;
        state.shops = action.payload.shops;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchShops.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addShop.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addShop.fulfilled, (state, action) => {
        state.loading = false;
        state.shops.push(action.payload);
        state.message = 'Shop added successfully';
      })
      .addCase(addShop.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchShopById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchShopById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedShop = action.payload;
      })
      .addCase(fetchShopById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateShop.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateShop.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.shops.findIndex((shop) => shop.id === action.payload.id);
        if (index !== -1) {
          state.shops[index] = action.payload;
        }
        state.message = 'Shop updated successfully';
      })
      .addCase(updateShop.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete a shop
      .addCase(deleteShop.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteShop.fulfilled, (state, action) => {
        state.loading = false;
        state.shops = state.shops.filter((shop) => shop._id !== action.payload);
        state.message = 'Shop deleted successfully';
      })
      .addCase(deleteShop.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setSelectedShop, clearSelectedShop, clearAlerts } = shopSlice.actions;
export default shopSlice.reducer;
